import React, { useEffect, useMemo, useState } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import {
  deleteReceipt,
  getcashorbank,
  getReceiptByGuid,
} from "../../services/AccountApiServices";
import Receipt from "../../components/accounting/RecieptModal";
import { ReceiptEntry } from "../../core/json/ReceiptEntry";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, money, num } from "../../components/report/reportFormat";

function ReceiptMaster() {
  const [receiptData, setReceiptData] = useState([]);
  const [updateData, setUpdateData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const [permit, setpermit] = useState();
  const { menuItems } = useSelector((state) => state.amc);

  useEffect(() => {
    handleLedgerList();
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Receipt")
    );
    handleReceiptList();
  }, []);

  const handleReceiptList = async () => {
    try {
      const response = await getReceiptByGuid(branch);
      setReceiptData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.log(error?.response?.data?.Message || "something went wrong");
      setReceiptData([]);
    }
  };

  const handleLedgerList = async () => {
    try {
      const response = await getcashorbank();
      if (Array.isArray(response?.data)) {
        setLedgerData(
          response?.data?.map((e) => ({
            value: e.guid,
            label: e.name,
          }))
        );
      } else {
        setLedgerData([]);
      }
    } catch (error) {
      console.log(error?.response?.data?.Message || "Something went wrong");
      setLedgerData([]);
    }
  };

  const handleEditClick = async (guid) => {
    try {
      const ReceiptByGuid = receiptData.find((data) => data.guid === guid);

      setUpdateData(ReceiptByGuid); // Set the data for editing
      setModalMode("edit");

      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching modifier reason type by GUID:",
        error?.response?.data?.Message
      );
    }
  };
  const handleDeleteClick = (obj) => {
    const objpayment = new ReceiptEntry();
    objpayment.Branch = branch;
    objpayment.guid = obj.guid;
    objpayment.id = obj.id;
    objpayment.updatedUser = userId?.id;
    objpayment.createdUser = userId?.id;
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteReceipt(objpayment);
          if (response.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The receipt has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handleReceiptList();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error("Error deleting sales quotation:", error);
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const MySwal = withReactContent(Swal);

  /* ---------------- list ---------------- */
  // one flat record per receipt; the keys match the column definitions
  const rows = useMemo(
    () =>
      receiptData.map((r) => ({
        guid: r.guid,
        raw: r,
        date: dateOf(r.date),
        billNo: r.no != null ? String(r.no) : "",
        cashBank: r.ledgerName || "",
        ledgerID: r.ledgerID,
        narration: r.narration || "",
        items: (r.receiptDetails || []).length,
        net: num(r.totalAmt),
        lines: r.receiptDetails || [],
      })),
    [receiptData]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "No", type: "doc" },
      { k: "cashBank", t: "Cash/Bank", type: "party" },
      { k: "narration", t: "Remarks" },
      { k: "items", t: "Lines", type: "int", off: true },
      { k: "net", t: "Amount", type: "money" },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Receipt" }], []);

  const filters = useMemo(
    () => [
      {
        key: "cashBank",
        label: "Cash/Bank",
        items: (ledgerData || []).map((l) => ({ v: l.value, t: l.label || "—" })),
        match: (r, v) => v.includes(r.ledgerID),
      },
    ],
    [ledgerData]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.cashBank) ||
      hit(r.narration) ||
      hit(r.date) ||
      String(r.net).includes(q) ||
      r.lines.some((l) => hit(l?.ledgerName) || hit(l?.narration))
    );
  };

  const drawerContent = (r) => ({
    title: r.cashBank || "Receipt",
    subtitle: [`Receipt ${r.billNo}`, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.cashBank,
    meta: [
      { k: "Cash/Bank", v: r.cashBank },
      { k: "Remarks", v: r.narration },
      { k: "Lines", v: intg(r.items) },
    ],
    lines: {
      title: "Receipt lines",
      columns: [{ t: "Ledger" }, { t: "Narration" }, { t: "Amount", align: "right" }],
      rows: r.lines.map((l) => [
        <div key="nm" className="axr-nm">{l?.ledgerName || "Unknown ledger"}</div>,
        l?.narration || "—",
        money(l?.amount),
      ]),
    },
    net: { l: "Total", v: money(r.net) },
  });

  return (
    <TransactionScreen
      title="Receipt"
      subtitle="Manage Receipt Type here"
      unit="receipts"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Receipt no, cash/bank, remarks, ledger, amount…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "net:-1", t: "Largest amount" },
        { v: "net:1", t: "Smallest amount" },
      ]}
      exportName="Receipt Master"
      onRefresh={handleReceiptList}
      canAdd={permit?.add}
      newLabel="New receipt"
      onNew={() => {
        setModalMode("add"); // Set the modal mode to 'add'
        setModalOpen(true); // Open the modal
      }}
      canEdit={permit?.edit}
      onEdit={(r) => handleEditClick(r.guid)}
      editLabel="Edit receipt"
      canDelete={permit?.deletion}
      onDelete={(r) => handleDeleteClick(r.raw)}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <Receipt
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handleReceiptList();
          }}
        />
      )}
    </TransactionScreen>
  );
}

export default ReceiptMaster;
